import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Mail, Shield, Briefcase } from "lucide-react";
import AssignmentFocus from "@/components/users/AssignmentFocus";

export default function ProfilePage({ currentUser, users }) {
  const navigate = useNavigate();

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-sm text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden" data-testid="profile-page">
      <ScrollArea className="h-full">
        <div className="max-w-2xl mx-auto p-8 space-y-8">
          <div className="flex items-center gap-3">
            <button
              data-testid="profile-back-btn"
              onClick={() => navigate(-1)}
              className="p-1.5 hover:bg-gray-100 rounded-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <h1
              className="text-2xl font-black tracking-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Profile
            </h1>
          </div>

          <div className="p-6 border border-gray-200 rounded-sm bg-white space-y-5">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-gray-200">
                <AvatarImage src={currentUser.avatar_url} alt={currentUser.full_name} />
                <AvatarFallback className="text-xl font-bold">
                  {currentUser.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2
                  className="text-xl font-bold"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                  data-testid="profile-name"
                >
                  {currentUser.full_name}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className="flex items-center gap-1.5 text-sm text-gray-500">
                    <Mail className="w-3.5 h-3.5" />
                    {currentUser.email}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Role
                </label>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-400" />
                  <span
                    className="text-sm font-semibold px-2 py-0.5 rounded-sm border"
                    style={{
                      backgroundColor: currentUser.role === "ADMIN" ? "#FEE2E2" : "#DBEAFE",
                      color: currentUser.role === "ADMIN" ? "#DC2626" : "#2563EB",
                      borderColor: currentUser.role === "ADMIN" ? "#FECACA" : "#BFDBFE",
                    }}
                  >
                    {currentUser.role}
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Often assigned to
                </label>
                <div className="flex items-start gap-2">
                  <Briefcase className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span data-testid="profile-assignment-focus">
                    <AssignmentFocus
                      labels={currentUser.assignment_focus}
                      className="text-sm font-medium text-gray-800"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
              All members
            </h3>
            <div className="border border-gray-200 rounded-sm divide-y divide-gray-100 bg-white">
              {(users || []).map((user) => (
                <div
                  key={user.id}
                  data-testid={`member-${user.id}`}
                  className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
                    user.id === currentUser.id ? "bg-gray-50" : ""
                  }`}
                >
                  <Avatar className="h-8 w-8 border border-gray-200">
                    <AvatarImage src={user.avatar_url} alt={user.full_name} />
                    <AvatarFallback className="text-xs font-semibold">
                      {user.full_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-black">{user.full_name}</span>
                      {user.id === currentUser.id && (
                        <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-sm">
                          YOU
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{user.email}</span>
                  </div>
                  <div className="flex-1 min-w-[120px] text-right sm:text-left">
                    <AssignmentFocus
                      labels={user.assignment_focus}
                      className="text-xs text-gray-600"
                      emptyText="—"
                    />
                  </div>
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm border"
                    style={{
                      backgroundColor: user.role === "ADMIN" ? "#FEE2E2" : "#F3F4F6",
                      color: user.role === "ADMIN" ? "#DC2626" : "#6B7280",
                      borderColor: user.role === "ADMIN" ? "#FECACA" : "#E5E7EB",
                    }}
                  >
                    {user.role || "MEMBER"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
